import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ALL_DATES_PILL_LABEL,
  DATE_FILTER_PILL_GAP,
  splitVisibleAndHiddenPillLabels,
} from './dateFilterOverflow';

const getPillLabel = (element: Element): string => {
  return element.getAttribute('data-date-pill-label')
    || element.getElementsByClassName('DateOption__label')?.[0]?.textContent
    || '';
};

interface UseDateFilterOverflowArgs {
  enabled: boolean;
  dates: string[];
  withoutAllDays?: boolean;
  isTogglePastMeetingsBtnShown: boolean;
  trackSectionWidth: number;
  selectedDate: string;
  showAllDates: boolean;
}

interface UseDateFilterOverflowResult {
  dateFilterRef: RefObject<HTMLDivElement>;
  headerFilterContent: RefObject<HTMLDivElement>;
  moreDatesMeasureRef: RefObject<HTMLButtonElement>;
  measuredHiddenDateLabels: string[];
}

export const useDateFilterOverflow = ({
  enabled,
  dates,
  withoutAllDays,
  isTogglePastMeetingsBtnShown,
  trackSectionWidth,
  selectedDate,
  showAllDates,
}: UseDateFilterOverflowArgs): UseDateFilterOverflowResult => {
  const [measuredHiddenDateLabels, setMeasuredHiddenDateLabels] = useState<string[]>([]);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const headerFilterContent = useRef<HTMLDivElement>(null);
  const moreDatesMeasureRef = useRef<HTMLButtonElement>(null);

  const measureOverflow = useCallback(() => {
    if (!enabled) return;

    const row = dateFilterRef.current;
    const container = headerFilterContent.current;
    if (!row || !container) return;

    const children = [...container.children] as HTMLElement[];

    children.forEach((child) => {
      child.style.display = '';
    });

    const rowWidth = row.getBoundingClientRect().width;
    const moreDatesWidth = moreDatesMeasureRef.current?.getBoundingClientRect().width ?? 0;
    const pillWidths = children.map((child) => ({
      label: getPillLabel(child),
      width: child.getBoundingClientRect().width,
    }));

    const gapsWidth = Math.max(0, children.length - 1) * DATE_FILTER_PILL_GAP;
    const totalPillWidth = pillWidths.reduce((sum, pill) => sum + pill.width, 0) + gapsWidth;
    const needsMoreDates = totalPillWidth > rowWidth;
    const availablePillWidth = needsMoreDates
      ? rowWidth - moreDatesWidth - DATE_FILTER_PILL_GAP
      : rowWidth;
    const { hiddenLabels } = splitVisibleAndHiddenPillLabels(
      pillWidths,
      availablePillWidth,
      DATE_FILTER_PILL_GAP,
    );

    children.forEach((child) => {
      const label = getPillLabel(child);
      const shouldHide = hiddenLabels.includes(label) && label !== ALL_DATES_PILL_LABEL;
      child.style.display = shouldHide ? 'none' : '';
    });

    const nextHiddenLabels = hiddenLabels.filter((label) => label !== ALL_DATES_PILL_LABEL);

    setMeasuredHiddenDateLabels((previous) => {
      if (previous.length === nextHiddenLabels.length
        && previous.every((label, index) => label === nextHiddenLabels[index])) {
        return previous;
      }
      return nextHiddenLabels;
    });
  }, [enabled]);

  useLayoutEffect(() => {
    if (!enabled) return;
    measureOverflow();
  }, [
    enabled,
    measureOverflow,
    dates,
    withoutAllDays,
    isTogglePastMeetingsBtnShown,
    trackSectionWidth,
    selectedDate,
    showAllDates,
  ]);

  useEffect(() => {
    if (!enabled) return undefined;

    const row = dateFilterRef.current;
    if (row && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        measureOverflow();
      });
      resizeObserver.observe(row);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [enabled, measureOverflow]);

  return {
    dateFilterRef,
    headerFilterContent,
    moreDatesMeasureRef,
    measuredHiddenDateLabels,
  };
};
